import React, { useState, useEffect } from 'react';
import { RefreshCw, Loader2, ChevronDown, ChevronRight, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as api from '../api';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MOIS_FR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const moisLabel = (key) => { const [y, m] = key.split('-'); return `${MOIS_FR[parseInt(m)-1]} ${y.slice(2)}`; };
const moisLabelFull = (key) => { const [y, m] = key.split('-'); return `${MOIS_FR[parseInt(m)-1]} ${y}`; };

const CELL_BORDER = '1px solid #E8EDF2';

function cs(sectionBg = false) {
  return {
    background: sectionBg ? '#F0F0EE' : 'white',
    color: 'var(--text)', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
    fontWeight: sectionBg ? 700 : 400,
    padding: '5px 8px', fontSize: 12, whiteSpace: 'nowrap',
    overflow: 'hidden', minWidth: 0, borderRight: CELL_BORDER,
  };
}
function hdrCs() {
  return { background: '#F8FAFC', color: 'var(--text-muted)', fontWeight: 700, textAlign: 'center',
           padding: '6px 8px', fontSize: 11, whiteSpace: 'nowrap', borderRight: CELL_BORDER };
}
const STICKY_FIRST = (bg = 'white') => ({
  position: 'sticky', left: 0, zIndex: 2, background: bg,
  padding: '5px 10px', fontSize: 12, whiteSpace: 'nowrap',
  overflow: 'hidden', borderRight: CELL_BORDER,
});

// Détermine la clé de colonne d'une échéance (miroir logique backend)
function colOfEcheance(e, currentMois, pastMois, futureMois) {
  const today = new Date();
  const lastDayCur = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  if (e.date_effective) {
    const d = new Date(e.date_effective + 'T12:00:00');
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    return key === currentMois ? 'cur_realise' : (pastMois.includes(key) || futureMois.includes(key) ? key : null);
  } else {
    const d = new Date(e.date_prevue + 'T12:00:00');
    if (d <= lastDayCur) return 'cur_avenir';
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    return futureMois.includes(key) ? key : null;
  }
}

// ── Lignes détail échéances ───────────────────────────────────────────────────
function EcheancesInline({ docId, docType, pastMois, futureMois, currentMois }) {
  const [echeances, setEcheances] = useState(null);
  const fetchFn = docType === 'ventes' ? api.getVenteEcheances : api.getAchatEcheances;
  useEffect(() => {
    fetchFn(docId).then(r => setEcheances(r.data || [])).catch(() => setEcheances([]));
  }, [docId]);

  if (!echeances) return (
    <tr><td colSpan={1 + pastMois.length + 2 + futureMois.length}
      style={{ padding: '6px 32px', background: '#FAFAF8' }}>
      <Loader2 size={11} className="animate-spin inline-block" style={{ color: 'var(--text-muted)' }} />
    </td></tr>
  );

  return echeances
    .filter(e => colOfEcheance(e, currentMois, pastMois, futureMois) !== null)
    .map(e => {
      const colKey = colOfEcheance(e, currentMois, pastMois, futureMois);
    const cols = [...pastMois, 'cur_realise', 'cur_avenir', ...futureMois];
    return (
      <tr key={e.echeance_id} style={{ background: '#FAFAF8', borderBottom: '1px solid #F0F0EE' }}>
        <td style={{ ...STICKY_FIRST('#FAFAF8'), paddingLeft: 28, fontSize: 11,
                     color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {e.libelle || '—'}
        </td>
        {cols.map(col => (
          <td key={col} style={{ ...cs(false), background: '#FAFAF8', fontSize: 11,
                                  color: colKey === col ? 'var(--text)' : 'transparent' }}>
            {fmt(colKey === col ? e.montant_ttc : 0)}
          </td>
        ))}
      </tr>
    );
  });
}

// ── Page principale ───────────────────────────────────────────────────────────
export default function PrevisionsTresorerie() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [collapsed, setCollapsed] = useState({ ventes: false, achats: false });
  const [expanded, setExpanded]   = useState({});
  const [exporting, setExporting] = useState(null); // 'excel' | 'pdf' | null
  const navigate = useNavigate();

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const fn = type === 'excel' ? api.exportPrevisionsTresorerieExcel : api.exportPrevisionsTresoreriePDF;
      const res = await fn();
      const ext  = type === 'excel' ? 'xlsx' : 'pdf';
      const mime = type === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `previsions_tresorerie_${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`Erreur export ${type.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try { const r = await api.getPrevisionsTresorerie(); setData(r.data); }
    catch { toast.error('Erreur chargement des prévisions'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="space-y-4 p-1">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)' }}>Prévisions de trésorerie</h1>
      <div className="flex items-center justify-center py-24">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--primary-solid)' }} />
      </div>
    </div>
  );
  if (!data) return null;

  const { mois, current_mois, ventes, achats, totaux_ventes, totaux_achats } = data;
  const pastMois   = mois.filter(m => m < current_mois);
  const futureMois = mois.filter(m => m > current_mois);
  const allDocs    = [...ventes, ...achats];

  const solde = { cur_realise: 0, cur_avenir: 0, mois: {} };
  solde.cur_realise = (totaux_ventes.cur_realise||0) - (totaux_achats.cur_realise||0);
  solde.cur_avenir  = (totaux_ventes.cur_avenir||0)  - (totaux_achats.cur_avenir||0);
  for (const m of mois) solde.mois[m] = ((totaux_ventes.mois[m]||0) - (totaux_achats.mois[m]||0));

  const getVal = (row, col) => {
    if (col === 'cur_realise') return row.cur_realise || 0;
    if (col === 'cur_avenir')  return row.cur_avenir  || 0;
    return row.mois[col] || 0;
  };

  const allExpanded = allDocs.every(d => expanded[d.doc_id]);
  const toggleAll   = () => {
    if (allExpanded) setExpanded({});
    else setExpanded(Object.fromEntries(allDocs.map(d => [d.doc_id, true])));
  };

  const renderRow = (row, isSection = false, sectionLabel = null, docType = null) => {
    const bg    = isSection ? '#F0F0EE' : 'white';
    const isExp = !isSection && !!expanded[row.doc_id];
    return (
      <React.Fragment key={row.doc_id || sectionLabel}>
        <tr style={{ borderBottom: '1px solid #F1F5F9', background: bg }}>
          <td style={{ ...STICKY_FIRST(bg), fontWeight: isSection ? 700 : 500, fontSize: isSection ? 13 : 12 }}>
            <div className="flex items-center justify-between gap-2 w-full">
              <div className="flex flex-col min-w-0">
                {isSection ? (
                  <span style={{ color: 'var(--text)', cursor: 'pointer' }}
                    onClick={() => setCollapsed(p => ({ ...p, [sectionLabel]: !p[sectionLabel] }))}>
                    {collapsed[sectionLabel] ? '▶' : '▼'} {sectionLabel === 'ventes' ? 'ENTRÉES' : 'SORTIES'}
                  </span>
                ) : <>
                  <span style={{ color: '#2563EB', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate(`/${docType}?${docType === 'ventes' ? 'open_devis' : 'open_achat'}=${row.doc_id}`)}>
                    {row.numero}
                  </span>
                  <span className="truncate text-xs" style={{ color: '#D97706' }}>{row.tiers_nom}</span>
                  {row.titre && (
                    <span className="truncate text-xs font-medium" style={{ color: '#16A34A' }}>{row.titre}</span>
                  )}
                </>}
              </div>
              {!isSection && (
                <button onClick={() => setExpanded(p => ({ ...p, [row.doc_id]: !p[row.doc_id] }))}
                  className="flex-shrink-0 p-0.5 rounded hover:bg-blue-50"
                  style={{ color: isExp ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
                  {isExp ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                </button>
              )}
            </div>
          </td>
          {pastMois.map(m => <td key={m} style={cs(isSection)}>{fmt(getVal(row, m))}</td>)}
          <td style={cs(isSection)}>{fmt(getVal(row, 'cur_realise'))}</td>
          <td style={cs(isSection)}>{fmt(getVal(row, 'cur_avenir'))}</td>
          {futureMois.map(m => <td key={m} style={cs(isSection)}>{fmt(getVal(row, m))}</td>)}
        </tr>
        {isExp && (
          <EcheancesInline docId={row.doc_id} docType={docType}
            pastMois={pastMois} futureMois={futureMois} currentMois={current_mois} />
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-4 p-1">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)' }}>Prévisions de trésorerie</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Échéances ventes &amp; achats — 10 mois glissants</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleAll}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            {allExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {allExpanded ? 'Tout replier' : 'Tout déplier'}
          </button>
          <button onClick={() => handleExport('excel')} disabled={!!exporting}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm hover:bg-green-50 disabled:opacity-50"
            style={{ borderColor: '#16A34A', color: '#16A34A' }}>
            {exporting === 'excel' ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />}
            Excel
          </button>
          <button onClick={() => handleExport('pdf')} disabled={!!exporting}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm hover:bg-red-50 disabled:opacity-50"
            style={{ borderColor: '#DC2626', color: '#DC2626' }}>
            {exporting === 'pdf' ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
            PDF
          </button>
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            <RefreshCw size={14} /> Actualiser
          </button>
        </div>
      </div>

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed',
            minWidth: `${320 + (pastMois.length + 2 + futureMois.length) * 140}px` }}>
            <colgroup>
              <col style={{ width: 320 }} />
              {pastMois.map(m => <col key={m} style={{ width: 140 }} />)}
              <col style={{ width: 140 }} />
              <col style={{ width: 140 }} />
              {futureMois.map(m => <col key={m} style={{ width: 140 }} />)}
            </colgroup>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--border)' }}>
                <th rowSpan={2} style={{ ...STICKY_FIRST(), fontWeight: 700, color: 'var(--text-muted)', textAlign: 'left', fontSize: 11, verticalAlign: 'middle' }}>
                  Document
                </th>
                {pastMois.map(m => (
                  <th key={m} rowSpan={2} style={{ ...hdrCs(), verticalAlign: 'middle' }}>{moisLabel(m)}</th>
                ))}
                <th colSpan={2} style={{ ...hdrCs(), textAlign: 'center', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                  {moisLabelFull(current_mois)}
                </th>
                {futureMois.map(m => (
                  <th key={m} rowSpan={2} style={{ ...hdrCs(), verticalAlign: 'middle' }}>{moisLabel(m)}</th>
                ))}
              </tr>
              <tr style={{ background: '#F8FAFC', borderBottom: '2px solid var(--border)' }}>
                <th style={hdrCs()}>Réalisé</th>
                <th style={hdrCs()}>À venir</th>
              </tr>
            </thead>
            <tbody>
              {renderRow(totaux_ventes, true, 'ventes')}
              {!collapsed.ventes && ventes.map(row => renderRow(row, false, null, 'ventes'))}
              {renderRow(totaux_achats, true, 'achats')}
              {!collapsed.achats && achats.map(row => renderRow(row, false, null, 'achats'))}
              <tr style={{ borderTop: '3px double var(--border)', background: '#F0F0EE' }}>
                <td style={{ ...STICKY_FIRST('#F0F0EE'), fontWeight: 800, fontSize: 13, color: 'var(--text)' }}>SOLDE</td>
                {pastMois.map(m => <td key={m} style={{ ...cs(true), fontWeight: 800, fontSize: 13, padding: '6px 8px' }}>{fmt(getVal(solde, m))}</td>)}
                <td style={{ ...cs(true), fontWeight: 800, fontSize: 13, padding: '6px 8px' }}>{fmt(solde.cur_realise)}</td>
                <td style={{ ...cs(true), fontWeight: 800, fontSize: 13, padding: '6px 8px' }}>{fmt(solde.cur_avenir)}</td>
                {futureMois.map(m => <td key={m} style={{ ...cs(true), fontWeight: 800, fontSize: 13, padding: '6px 8px' }}>{fmt(getVal(solde, m))}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
